import { FC } from 'react';
import Head from 'next/head';
import LogoShareOg from '/public/icons/shared/logo-share-og.png';
import getConfig from 'next/config';

interface SeoProps {
  title: string;
  description: string;
  image?: string;
  canonical: string;
  pagePath: string;
  /**
   * CMS indexing flags, in the CMS's own positive polarity.
   *
   * These used to be `noIndex`/`noFollow` props fed straight from the CMS's
   * `hasIndex`/`hasFollow` fields — i.e. `noIndex={seoPage?.hasIndex}`, which
   * inverted the meaning at all 15 call sites. Matching the prop name and
   * polarity to the CMS field makes that class of mistake unrepresentable.
   *
   * Optional because the cinema payload omits them entirely.
   *
   * Currently accepted but NOT consumed — see the robots-tag comment in the
   * body for why, and what has to happen before they can be.
   */
  hasIndex?: boolean;
  hasFollow?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components?: () => void | any;
}

/**
 * It's a function that takes in a bunch of props and returns a bunch of meta tags.
 * @param  - FC<SeoProps> = ({
 * @returns A React component.
 */
const Seo: FC<SeoProps> = ({
  description,
  title,
  image,
  pagePath,
  canonical,
  components,
}) => {
  const { publicRuntimeConfig } = getConfig();
  const { nextPublicBaseUrl } = publicRuntimeConfig;

  const handleCanonicalUrl = (url: string): string => url.split(/[?#]/)[0];

  // Share crawlers (Facebook, WhatsApp, X) only accept absolute image URLs, and
  // /images/... from /public used to go out as is.
  const shareImage = image
    ? image.startsWith('/')
      ? new URL(image, nextPublicBaseUrl).toString()
      : image
    : `${nextPublicBaseUrl}${LogoShareOg.src.slice(1)}`;

  // A CMS-supplied canonical wins; otherwise fall back to the page's own URL.
  //
  // This used to read `canonical || pagePath === '/' ? '' : ...`. `||` binds
  // tighter than `?:`, so it parsed as `(canonical || pagePath === '/') ? '' : ...`
  // and returned the empty string whenever `canonical` was set — i.e. every page
  // the CMS supplied a canonical for shipped `<link rel="canonical" href="">`,
  // the homepage included.
  const canonicalUrl = handleCanonicalUrl(
    canonical ||
      (pagePath === '/' ? nextPublicBaseUrl : `${nextPublicBaseUrl}${pagePath}`)
  );

  return (
    <Head>
      <meta name='viewport' content='width=device-width, initial-scale=1.0' />
      <title>{title}</title>
      {/* Omit the tag entirely rather than emit an empty href, which search
      engines treat as a self-referential canonical pointing at nothing. */}
      {!!canonicalUrl && <link rel='canonical' href={canonicalUrl} />}
      {/* It's a link tag that is used to tell search engines that there are other versions
      of this page in other languages.  */}
      <link
        rel='alternate'
        href={handleCanonicalUrl(`${nextPublicBaseUrl}en${pagePath?.slice(2)}`)}
        hrefLang='en'
      />
      <link
        rel='alternate'
        href={handleCanonicalUrl(`${nextPublicBaseUrl}en${pagePath?.slice(2)}`)}
        hrefLang='x-default'
      />
      <link
        rel='alternate'
        href={handleCanonicalUrl(`${nextPublicBaseUrl}ar${pagePath?.slice(2)}`)}
        hrefLang='ar'
      />
      <meta name='description' content={description} />
      <meta name='image' content={shareImage} />

      <meta property='image:width' content='250' />
      <meta property='image:height' content='250' />
      {/*  facebook */}
      <meta property='og:title' content={title} />
      <meta property='og:description' content={description} />
      <meta property='og:image' content={shareImage} />
      <meta property='og:image:width' content='250' />
      <meta property='og:image:height' content='250' />
      <meta
        property='og:url'
        content={`${nextPublicBaseUrl}${pagePath === '/' ? '' : pagePath}`}
      />
      <meta property='og:type' content='article' />
      <meta property='og:site_name' content='muvi Cinemas' />
      {/*  twitter */}
      <meta property='twitter:card' content='summary_large_image' />
      <meta property='twitter:site' content='@muvicinemas' />
      <meta property='twitter:creator' content='@muvicinemas' />
      <meta name='twitter:title' content={title} />
      <meta name='twitter:description' content={description} />
      <meta name='twitter:image' content={shareImage} />
      <meta property='twitter:image:width' content='250' />
      <meta property='twitter:image:height' content='250' />
      <meta
        property='twitter:url'
        content={`${nextPublicBaseUrl}${pagePath === '/' ? '' : pagePath}`}
      />
      {/* DELIBERATELY NOT RENDERING A ROBOTS TAG YET.
      //
      // The correct implementation is:
      //
      //   {(hasIndex === false || hasFollow === false) && (
      //     <meta name='robots' content={[
      //       hasIndex === false ? 'noindex' : null,
      //       hasFollow === false ? 'nofollow' : null,
      //     ].filter(Boolean).join(',')} />
      //   )}
      //
      // It cannot be enabled until the CMS data is corrected. As of 2026-08-23
      // the API returns `hasIndex: false, hasFollow: false` for ALL 12 static
      // pages (HOME included) and for every movie, offer and experience —
      // verified in __NEXT_DATA__ on production. Cinema pages omit the fields.
      //
      // So the flags are not "unset defaults": they actively say noindex
      // everything. Shipping the block above against today's data would
      // deindex nearly the whole site on deploy. Historically that was masked
      // by the props being inverted at all 15 call sites
      // (`noIndex={seoPage?.hasIndex}`), which turned false into "no tag".
      // That inversion is now removed, so the masking is gone too.
      //
      // TO ENABLE (both steps, in this order):
      //   1. Correct the CMS so public pages return hasIndex/hasFollow = true,
      //      leaving false only where a noindex is genuinely intended.
      //   2. Verify on staging that / , /en/cinemas and a movie page emit NO
      //      robots tag, then swap this comment for the block above.
      //
      // Until step 1 lands, emitting nothing preserves today's behaviour
      // (everything indexable), which is what production already relies on. */}
      {!!components && components()}
    </Head>
  );
};

export default Seo;
